// Dedupes QuizStart across reloads.
//
// init() in useQuizWizardController.ts runs on every mount of /quiz against a get-or-create
// endpoint (Django's get_or_create_active_instance always returns 201, whether it created a
// new instance or resumed an in-progress one — there is no created/resumed flag in the
// response to key off). Without this guard, a reload mid-quiz, a back-nav to /quiz, or
// resuming tomorrow's draft would each re-fire QuizStart against the same questionnaire,
// undercounting the true completion rate this instrumentation exists to produce.
//
// The questionnaire id is stable for the life of one in-progress questionnaire and changes
// when a genuinely new quiz begins (a fresh instance, or the session-recovery path creating a
// replacement id) — so it's the right key to dedupe on, not a request-scoped ref.

const STORAGE_KEY = "bsa_quiz_started_ids";

// Caps unbounded growth from someone starting many quizzes over time; old entries are dropped
// first (FIFO) since only recent starts are ever re-visited within a reload/back-nav window.
const MAX_STORED_IDS = 50;

function readStartedIds(): number[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is number => typeof v === "number") : [];
  } catch {
    return [];
  }
}

/** Returns true the first time this questionnaire id is seen (caller should fire QuizStart),
 * false on every subsequent call for the same id (reload, back-nav, next-day resume). */
export function markQuizStarted(id: number): boolean {
  if (typeof window === "undefined") return false;
  const ids = readStartedIds();
  if (ids.includes(id)) return false;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids, id].slice(-MAX_STORED_IDS)));
  } catch {
    // Private-mode Safari or storage disabled — can't persist, so this id may re-fire next
    // reload. Degrades to today's behavior for that visitor rather than throwing.
  }
  return true;
}
